import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const ShareExportModal = ({ isOpen, onClose, claim }) => {
  const [activeTab, setActiveTab] = useState('share');
  const [shareOptions, setShareOptions] = useState({
    includeEvidence: true,
    includeTimeline: true,
    includeMetadata: true,
    publicLink: true
  });
  const [exportFormat, setExportFormat] = useState('pdf');
  const [customMessage, setCustomMessage] = useState('');

  if (!isOpen) return null;

  const shareUrl = `https://truthguard.eu/claim/${claim?.id}`;
  const citationText = `TruthGuard EU. "${claim?.statement}" Fact-check Analysis. ${new Date()?.toLocaleDateString('en-GB')}. ${shareUrl}`;

  const handleCopyLink = () => {
    navigator.clipboard?.writeText(shareUrl);
    // Show success toast
  };

  const handleCopyCitation = () => {
    navigator.clipboard?.writeText(citationText);
    // Show success toast
  };

  const handleSocialShare = (platform) => {
    const text = encodeURIComponent(`Fact-check: ${claim?.statement} - Verdict: ${claim?.verdict}`);
    const url = encodeURIComponent(shareUrl);
    
    let shareLink = '';
    switch (platform) {
      case 'twitter':
        shareLink = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
        break;
      case 'facebook':
        shareLink = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case 'linkedin':
        shareLink = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
        break;
      case 'whatsapp':
        shareLink = `https://wa.me/?text=${text}%20${url}`;
        break;
    }
    
    if (shareLink) {
      window.open(shareLink, '_blank', 'width=600,height=400');
    }
  };

  const handleExport = () => {
    // Export functionality would be implemented here
    console.log('Exporting in format:', exportFormat);
    onClose();
  };

  const renderShareTab = () => (
    <div className="space-y-6">
      {/* Quick Share Options */}
      <div>
        <h4 className="font-medium text-foreground mb-3">Quick Share</h4>
        <div className="grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            iconName="Twitter"
            iconPosition="left"
            onClick={() => handleSocialShare('twitter')}
          >
            Twitter
          </Button>
          <Button
            variant="outline"
            iconName="Facebook"
            iconPosition="left"
            onClick={() => handleSocialShare('facebook')}
          >
            Facebook
          </Button>
          <Button
            variant="outline"
            iconName="Linkedin"
            iconPosition="left"
            onClick={() => handleSocialShare('linkedin')}
          >
            LinkedIn
          </Button>
          <Button
            variant="outline"
            iconName="MessageCircle"
            iconPosition="left"
            onClick={() => handleSocialShare('whatsapp')}
          >
            WhatsApp
          </Button>
        </div>
      </div>

      {/* Direct Link */}
      <div>
        <h4 className="font-medium text-foreground mb-3">Direct Link</h4>
        <div className="flex items-center space-x-2">
          <Input
            value={shareUrl}
            readOnly
            className="flex-1"
          />
          <Button
            variant="outline"
            iconName="Copy"
            onClick={handleCopyLink}
          >
            Copy
          </Button>
        </div>
      </div>

      {/* Custom Message */}
      <div>
        <h4 className="font-medium text-foreground mb-3">Add Custom Message</h4>
        <textarea
          value={customMessage}
          onChange={(e) => setCustomMessage(e?.target?.value)}
          placeholder="Add your own message to accompany this fact-check..."
          className="w-full h-24 px-3 py-2 border border-border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {/* Share Options */}
      <div>
        <h4 className="font-medium text-foreground mb-3">Include in Share</h4>
        <div className="space-y-2">
          <Checkbox
            label="Evidence and sources"
            checked={shareOptions?.includeEvidence}
            onChange={(e) => setShareOptions({...shareOptions, includeEvidence: e?.target?.checked})}
          />
          <Checkbox
            label="Verdict timeline"
            checked={shareOptions?.includeTimeline}
            onChange={(e) => setShareOptions({...shareOptions, includeTimeline: e?.target?.checked})}
          />
          <Checkbox
            label="Metadata and statistics"
            checked={shareOptions?.includeMetadata}
            onChange={(e) => setShareOptions({...shareOptions, includeMetadata: e?.target?.checked})}
          />
          <Checkbox
            label="Create public shareable link"
            checked={shareOptions?.publicLink}
            onChange={(e) => setShareOptions({...shareOptions, publicLink: e?.target?.checked})}
          />
        </div>
      </div>
    </div>
  );

  const renderExportTab = () => (
    <div className="space-y-6">
      {/* Export Format */}
      <div>
        <h4 className="font-medium text-foreground mb-3">Export Format</h4>
        <div className="grid grid-cols-2 gap-3">
          {[
            { id: 'pdf', label: 'PDF Report', icon: 'FileText', description: 'Complete analysis with formatting' },
            { id: 'json', label: 'JSON Data', icon: 'Code', description: 'Machine-readable structured data' },
            { id: 'csv', label: 'CSV Export', icon: 'Table', description: 'Tabular data for analysis' },
            { id: 'bibtex', label: 'BibTeX Citation', icon: 'GraduationCap', description: 'Academic citation format' }
          ]?.map((format) => (
            <button
              key={format?.id}
              onClick={() => setExportFormat(format?.id)}
              className={`
                p-4 border rounded-lg text-left transition-smooth
                ${exportFormat === format?.id
                  ? 'border-primary bg-primary/5 text-primary' :'border-border hover:border-primary/50'
                }
              `}
            >
              <div className="flex items-center space-x-2 mb-2">
                <Icon name={format?.icon} size={18} />
                <span className="font-medium">{format?.label}</span>
              </div>
              <p className="text-xs text-muted-foreground">{format?.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Citation Preview */}
      <div>
        <h4 className="font-medium text-foreground mb-3">Citation Preview</h4>
        <div className="p-4 bg-muted/30 rounded-lg border border-border">
          <p className="text-sm text-foreground font-mono">{citationText}</p>
          <Button
            variant="ghost"
            size="sm"
            iconName="Copy"
            iconPosition="left"
            onClick={handleCopyCitation}
            className="mt-2"
          >
            Copy Citation
          </Button>
        </div>
      </div>

      {/* Export Options */}
      <div>
        <h4 className="font-medium text-foreground mb-3">Export Options</h4>
        <div className="space-y-2">
          <Checkbox
            label="Include full evidence attachments"
            checked={shareOptions?.includeEvidence}
            onChange={(e) => setShareOptions({...shareOptions, includeEvidence: e?.target?.checked})}
          />
          <Checkbox
            label="Include forensics analysis results"
            checked={shareOptions?.includeTimeline}
            onChange={(e) => setShareOptions({...shareOptions, includeTimeline: e?.target?.checked})}
          />
          <Checkbox
            label="Include C2PA verification data"
            checked={shareOptions?.includeMetadata}
            onChange={(e) => setShareOptions({...shareOptions, includeMetadata: e?.target?.checked})}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Modal */}
      <div className="relative bg-card border border-border rounded-lg shadow-elevated w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">Share & Export Analysis</h3>
          <Button
            variant="ghost"
            size="icon"
            iconName="X"
            onClick={onClose}
          />
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-border">
          <nav className="flex space-x-1 p-1 mx-6">
            <button
              onClick={() => setActiveTab('share')}
              className={`
                flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-smooth
                ${activeTab === 'share' ?'bg-primary text-primary-foreground shadow-soft' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                }
              `}
            >
              <Icon name="Share" size={16} />
              <span>Share</span>
            </button>
            <button
              onClick={() => setActiveTab('export')}
              className={`
                flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-smooth
                ${activeTab === 'export'
                  ? 'bg-primary text-primary-foreground shadow-soft' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                }
              `}
            >
              <Icon name="Download" size={16} />
              <span>Export</span>
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="p-6 max-h-96 overflow-y-auto">
          {activeTab === 'share' ? renderShareTab() : renderExportTab()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/30">
          <div className="text-sm text-muted-foreground">
            Analysis ID: {claim?.id}
          </div>
          <div className="flex items-center space-x-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            {activeTab === 'export' && (
              <Button variant="default" onClick={handleExport}>
                Export {exportFormat?.toUpperCase()}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareExportModal;